/**
 * Schedule comparison utilities — the single source of truth for building
 * current-vs-proposed comparison rows and the per-row calculation-detail used
 * by the shared ScheduleComparisonTable.
 *
 * Consistency note (financial system): every impact preview must compute and
 * present the same quantities the same way. Both the current and proposed sides
 * derive their interest formula via buildRealMethodCalcDetail using the
 * contract's REAL calculation method (Simple / Compound / Compound Linear) — we
 * never fall back to a flat formula for a compound contract.
 */

import { formatNumber } from './formatters';

/**
 * Match any kind of interest payment narration (regular, adhoc, or combined).
 * Combined Payment folds Interest + Capital into one row (T064) but caps an
 * accrual period exactly like an interest payment.
 */
export const isInterestPaymentNarration = (narration) => {
  if (!narration) return false;
  return narration === 'Interest Payment'
    || narration === 'Adhoc Interest Payment'
    || narration === 'Combined Payment';
};

/** Accrual or payment (regular/adhoc/combined). */
export const isInterestNarration = (narration) => {
  if (!narration) return false;
  return narration.startsWith('Interest Accrual') || isInterestPaymentNarration(narration);
};

/** Coarse bucket used to align current rows with proposed rows. */
export const narrationClass = (narration) => {
  const n = narration || '';
  if (n.startsWith('Interest Accrual')) return 'accrual';
  if (isInterestPaymentNarration(n)) return 'payment';
  return 'other';
};

/** Inflow / outflow / neutral, for signed-amount colouring. */
export const getFlowType = (narration) => {
  if (!narration) return 'neutral';
  if (narration.includes('Drawdown') || narration.includes('Opening Balance')) return 'inflow';
  if (narration.includes('Payment') || narration.includes('Repayment') || narration.includes('Withdrawal')) return 'outflow';
  return 'neutral';
};

/**
 * Build a calc-detail object using the contract's REAL interest method, so the
 * Σ popover reconciles to the displayed value. Drives BOTH the current (booked)
 * and proposed sides. Mirrors the backend buildFormulaDisplay branches.
 *
 * @param {object} args
 * @param {number} args.principal
 * @param {number} args.rate            annual rate %
 * @param {number} args.days
 * @param {number} args.dayCountBasis
 * @param {string} args.calcType        'Simple' | 'Compound' | 'Compound Linear' (or normalized variants)
 * @param {number} args.compoundingFrequency
 * @param {number} args.calculatedValue the value the formula should equal
 * @param {number} args.dp              rounding precision for display
 * @param {'current'|'proposed'} args.mode
 * @param {number} [args.auditDailyRate] exact Compound-Linear daily rate from backend _audit
 */
export const buildRealMethodCalcDetail = ({
  principal, rate, days, dayCountBasis, calcType, compoundingFrequency,
  calculatedValue, dp = 3, mode = 'current', auditDailyRate = null,
}) => {
  const p = parseFloat(principal) || 0;
  const r = parseFloat(rate) || 0;
  const d = parseInt(days, 10) || 0;
  const basis = parseInt(dayCountBasis, 10) || 365;
  const result = parseFloat(calculatedValue) || 0;
  const ct = calcType || 'Simple';

  let normalizedType = 'simple_interest';
  let formula_display;
  const input_params = { principal: p, rate: r, days: d, day_count_basis: basis };

  if (ct === 'Compound' || ct === 'compound_interest') {
    const n = parseInt(compoundingFrequency, 10) || 12;
    normalizedType = 'compound_interest';
    input_params.compounding_frequency = n;
    formula_display =
      `R ${formatNumber(p, dp)} x ((1 + ${r}% / ${n})^(${n} x ${d} / ${basis}) - 1) = R ${formatNumber(result, dp)}`;
  } else if (ct === 'Compound Linear' || ct === 'compound_linear') {
    normalizedType = 'compound_linear';
    input_params.daily_rate = auditDailyRate != null
      ? parseFloat(auditDailyRate)
      : (basis > 0 ? (p * (r / 100)) / basis : 0);
    formula_display = `FV daily rate x ${d} days = R ${formatNumber(result, dp)}`;
  } else {
    formula_display =
      `R ${formatNumber(p, dp)} x ${r}% x ${d} / (${basis} x 100) = R ${formatNumber(result, dp)}`;
  }

  return { calc_type: normalizedType, mode, input_params, formula_display, calculated_value: result };
};

/**
 * Build a calc-detail for one schedule accrual row. Generated/projected rows
 * carry an `_audit` block (principal, day_count_basis, compounding_frequency,
 * compound_linear_daily_rate); current (DB) rows do not, so we fall back to the
 * contract-level calc context. Either way the REAL method is used.
 */
const accrualDetail = (row, ctx, mode) => {
  if (!row) return null;
  const audit = row._audit || {};
  return buildRealMethodCalcDetail({
    principal: audit.principal != null ? audit.principal : row.outstanding_balance,
    rate: row.interest_rate,
    days: row.days_in_period,
    dayCountBasis: audit.day_count_basis != null ? audit.day_count_basis : ctx.dayCountBasis,
    calcType: row.interest_calc_type || ctx.calcType,
    compoundingFrequency: audit.compounding_frequency != null ? audit.compounding_frequency : ctx.compoundingFrequency,
    calculatedValue: parseFloat(row.gross_interest_amount != null ? row.gross_interest_amount : row.interest_amount) || 0,
    dp: ctx.dp,
    mode,
    auditDailyRate: audit.compound_linear_daily_rate,
  });
};

const grossOf = (row) => parseFloat(
  row.gross_interest_amount != null ? row.gross_interest_amount : row.interest_amount,
) || 0;
const netOf = (row) => parseFloat(
  row.net_interest_amount != null ? row.net_interest_amount
    : (row.payment_amount != null ? row.payment_amount : row.interest_amount),
) || 0;

/**
 * Generic current-vs-proposed row builder for amendment / regeneration previews
 * (no rate-effective-date split — a capital/maturity change simply alters
 * downstream balances). Joins the current schedule against the projected
 * (backend real-method) schedule by (date, narration-class) using a FIFO queue
 * per key, so repeated dates align in document order.
 *
 * Returns rows in the shape ScheduleComparisonTable expects.
 *
 * @param {Array} currentSchedule  rich current rows (fn_schedule_get_current_with_narration)
 * @param {Array} projectedSchedule generated rows (carry _audit)
 * @param {object} ctx { calcType, compoundingFrequency, dayCountBasis, dp }
 */
export const buildComparisonRows = (currentSchedule, projectedSchedule, ctx = {}) => {
  const cur = currentSchedule || [];
  const proj = projectedSchedule || [];
  const context = {
    calcType: ctx.calcType || 'Simple',
    compoundingFrequency: ctx.compoundingFrequency || 12,
    dayCountBasis: ctx.dayCountBasis || 365,
    dp: ctx.dp ?? 3,
  };
  const tol = 0.01;

  // Full-outer merge by (date, narration-class). A capital event changes the
  // schedule's SHAPE (extra withdrawal/injection row, shifted/split accruals),
  // so current and projected don't align 1:1 — we must show a row whenever
  // EITHER side has an entry, never silently dropping projected rows.
  //
  // Bucket both sides by key, preserving first-appearance order across the
  // merged document, then zip the two FIFO queues per key (current[i] with
  // projected[i]); surplus on either side becomes one-sided rows.
  const keyOf = (e) => `${String(e.payment_date || '').substring(0, 10)}|${narrationClass(e.narration)}`;

  const order = [];
  const seen = new Set();
  const curQ = new Map();
  const projQ = new Map();
  const pushBucket = (map, e) => {
    const k = keyOf(e);
    if (!seen.has(k)) { seen.add(k); order.push(k); }
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(e);
  };
  // Interleave by walking current then projected, but order[] is seeded in the
  // order keys first appear across current ++ projected so dates stay sorted-ish
  // and event rows land where they occur.
  cur.forEach((e) => pushBucket(curQ, e));
  proj.forEach((e) => pushBucket(projQ, e));

  // Chronological order; within a date keep accrual before payment before other
  // so a period reads accrue → pay → (capital event) like the schedule itself.
  const CLASS_RANK = { accrual: 0, payment: 1, other: 2 };
  order.sort((a, b) => {
    const [da, ca] = a.split('|');
    const [db, cb] = b.split('|');
    if (da !== db) return da < db ? -1 : 1;
    return (CLASS_RANK[ca] ?? 9) - (CLASS_RANK[cb] ?? 9);
  });

  const num = (v) => (v == null ? null : parseFloat(v));

  const buildRow = (entry, pv, cls) => {
    const base = entry || pv; // at least one is non-null
    const narration = (base.narration) || '';
    const flow_type = getFlowType(narration);
    const capAmt = num((entry || {}).capital_amount) ?? num((pv || {}).capital_amount) ?? 0;

    if (cls === 'other') {
      // Capital/drawdown/withdrawal/payment rows: no interest comparison, but we
      // still show whichever side(s) exist plus the balance, and flag a row that
      // exists on only one side as impacted (it IS a structural change).
      const curInt = num((entry || {}).interest_amount) ?? num((entry || {}).net_interest_amount);
      const propInt = num((pv || {}).interest_amount) ?? num((pv || {}).net_interest_amount);
      const oneSided = (!entry) !== (!pv);
      return {
        payment_date: base.payment_date,
        narration,
        current_rate: entry ? entry.interest_rate : null,
        proposed_rate: pv ? num(pv.interest_rate) : null,
        days_in_period: null, // non-interest rows (capital/drawdown/event) have no accrual period
        current_interest: entry ? curInt : null,
        proposed_interest: pv ? propInt : null,
        capital_amount: (entry || pv).capital_amount,
        current_capital: entry ? num(entry.capital_amount) : null,
        proposed_capital: pv ? num(pv.capital_amount) : null,
        current_total: entry ? ((curInt || 0) + (num(entry.capital_amount) || 0)) || null : null,
        proposed_total: pv ? ((propInt || 0) + (num(pv.capital_amount) || 0)) || null : null,
        current_balance: entry ? num(entry.outstanding_balance) : null,
        proposed_balance: pv ? num(pv.outstanding_balance) : null,
        is_impacted: oneSided,
        flow_type,
      };
    }

    const currentInterest = entry ? (num(entry.interest_amount) ?? num(entry.net_interest_amount) ?? 0) : null;
    const proposedVal = pv == null ? null : (cls === 'accrual' ? grossOf(pv) : netOf(pv));
    const is_impacted = (currentInterest != null && proposedVal != null)
      ? Math.abs(currentInterest - proposedVal) > tol
      : (!entry) !== (!pv); // one-sided row is itself a change
    return {
      payment_date: base.payment_date,
      narration,
      current_rate: entry ? entry.interest_rate : null,
      proposed_rate: pv != null && pv.interest_rate != null ? parseFloat(pv.interest_rate) : null,
      days_in_period: cls === 'accrual' ? ((entry || pv).days_in_period) : null,
      current_interest: entry ? (entry.interest_amount || entry.net_interest_amount) : null,
      proposed_interest: proposedVal,
      capital_amount: (entry || pv).capital_amount,
      current_capital: entry ? num(entry.capital_amount) : null,
      proposed_capital: pv ? num(pv.capital_amount) : null,
      current_total: entry ? ((currentInterest || 0) + (capAmt || 0)) || null : null,
      proposed_total: pv != null ? ((proposedVal || 0) + (capAmt || 0)) || null : null,
      current_balance: entry ? num(entry.outstanding_balance) : null,
      proposed_balance: pv ? num(pv.outstanding_balance) : null,
      is_impacted,
      flow_type,
      current_calc_detail: (entry && cls === 'accrual') ? accrualDetail(entry, context, 'current') : undefined,
      calc_detail: (pv && cls === 'accrual') ? accrualDetail(pv, context, 'proposed') : undefined,
    };
  };

  const rows = [];
  order.forEach((key) => {
    const cls = key.split('|')[1];
    const cq = curQ.get(key) || [];
    const pq = projQ.get(key) || [];
    const n = Math.max(cq.length, pq.length);
    for (let i = 0; i < n; i += 1) {
      rows.push(buildRow(cq[i] || null, pq[i] || null, cls));
    }
  });

  return rows;
};
